import { useCallback, useEffect, useState } from 'react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ContentPageLayout } from 'root/src/client/v2/_pages/content/redesign/ContentPageLayout';
import { faqSections } from 'root/src/client/v2/_pages/faq/redesign/faqContent';
import { FaqAccordionSection } from 'root/src/client/v2/_pages/faq/redesign/FaqAccordionSection';
import { FaqTableOfContents } from 'root/src/client/v2/_pages/faq/redesign/FaqTableOfContents';
const FAQ_SUBTITLE = 'Find quick answers to your most pressing questions. Or, contact us.';
// Every valid question id (used to validate a URL hash before opening / scrolling to it).
const questionIds = new Set(faqSections.flatMap(section => section.items.map(item => item.id)));
// Smooth-scroll to an anchor target id. The `AnchorTarget` span carries the id and is itself offset
// `-88px` above its heading, so scrolling it to the viewport top lands the heading clear of the fixed
// navbar. Honours reduced motion by jumping instantly.
const scrollToId = (id) => {
    const target = document.getElementById(id);
    if (!target) {
        return;
    }
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
};
// FAQ. REUSES the verbatim legacy `FAQ/pageContent.js` array (Q&A wording, `<Link>`s, the
// `{daysToExpire}` interpolation); only the presentation is new. Rendered as a redesign accordion
// grouped by the four sections, with a sticky category + question TOC sidebar (the shared content-page
// chrome, same as the legal pages). Deep links: a `#<question-id>` in the URL opens that accordion item
// and scrolls to it on mount (matching the legacy hash behaviour); a TOC question click opens its item
// before smooth-scrolling to it. Controlled open state is shared across sections so any number of
// answers can stay open. Zero layout shift, reduced motion respected.
export const RedesignFaqPage = () => {
    const [openItems, setOpenItems] = useState([]);
    const openItem = useCallback((id) => {
        setOpenItems(prev => (prev.includes(id) ? prev : [...prev, id]));
    }, []);
    // Deep link: open + scroll to the question in the URL hash on mount.
    useEffect(() => {
        const hash = window.location.hash.replace('#', '');
        if (!hash || !questionIds.has(hash)) {
            return undefined;
        }
        openItem(hash);
        // Defer the scroll until the item has expanded so the target lands in its final position.
        const timer = window.setTimeout(() => scrollToId(hash), 100);
        return () => window.clearTimeout(timer);
    }, [openItem]);
    // TOC navigation: open the target question (if any) first, then scroll once it has expanded so the
    // target lands in its final position. Section headings scroll immediately.
    const handleNavigate = useCallback((id, isQuestion) => {
        if (isQuestion) {
            openItem(id);
            window.setTimeout(() => scrollToId(id), 100);
        }
        else {
            scrollToId(id);
        }
    }, [openItem]);
    return (<ContentPageLayout icon={HelpOutlineIcon} title="FAQ" subtitle={FAQ_SUBTITLE} tableOfContents={<FaqTableOfContents onNavigate={handleNavigate}/>}>
			<div className="space-y-2">
				{faqSections.map(section => (<FaqAccordionSection key={section.id} section={section} openItems={openItems} onOpenChange={setOpenItems}/>))}
			</div>
		</ContentPageLayout>);
};
